import React, { useState } from "react";

const sections = [
  {
    id: 1,
    title: "Information We Collect",
    content: (
      <>
        <p>We collect the following categories of information when you use Tracklio Hub:</p>
        <div className="info-grid">
          <div className="info-card">
            <div className="info-icon">👤</div>
            <strong>Account Information</strong>
            <p>Full name, email address, phone number, company name, designation, and profile photo provided during registration.</p>
          </div>
          <div className="info-card">
            <div className="info-icon">💳</div>
            <strong>Billing & Payment</strong>
            <p>Subscription plan details, billing address, GST number. Payment processing is handled by third-party gateways (Razorpay, Cashfree). We do not store card details.</p>
          </div>
          <div className="info-card">
            <div className="info-icon">📱</div>
            <strong>Device & MDM Data</strong>
            <p>For MDM module users: device IMEI, Android ID, serial number, device model, OS version, GPS location (if enabled), and app usage data from enrolled devices.</p>
          </div>
          <div className="info-card">
            <div className="info-icon">🌐</div>
            <strong>Usage & Technical Data</strong>
            <p>IP address, browser type, pages visited, session duration, error logs, and feature usage analytics to improve platform performance.</p>
          </div>
          <div className="info-card">
            <div className="info-icon">🏢</div>
            <strong>Business Data</strong>
            <p>Data you enter into our platform modules: customer records (CRM), employee records (HRMS), inventory items, hospital patient data, orders, invoices, and related business information.</p>
          </div>
          <div className="info-card">
            <div className="info-icon">📧</div>
            <strong>Communication Data</strong>
            <p>Messages sent through our support channels, emails, feedback forms, and in-app communication logs.</p>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 2,
    title: "How We Use Your Information",
    content: (
      <>
        <p>Information collected is used exclusively for the following purposes:</p>
        <ul>
          <li><strong>Service Delivery:</strong> To provide, operate, and maintain all Tracklio Hub platform modules including CRM, HRMS, Hospital Management, MDM, Inventory, Wallet, and more.</li>
          <li><strong>Account Management:</strong> To create and manage your account, subscriptions, and user hierarchy.</li>
          <li><strong>Billing & Invoicing:</strong> To process subscription payments, generate invoices, and manage refunds.</li>
          <li><strong>MDM & Device Management:</strong> To track, manage, and enforce policies on enrolled enterprise devices as authorized by your organization.</li>
          <li><strong>Platform Improvement:</strong> To analyze usage patterns, fix bugs, improve features, and develop new modules.</li>
          <li><strong>Security & Compliance:</strong> To detect fraud, prevent unauthorized access, and meet legal obligations.</li>
          <li><strong>Communication:</strong> To send transactional emails (invoices, alerts, password resets), service announcements, and support responses.</li>
          <li><strong>White-Label Services:</strong> To enable platform customization for white-label clients and their end users.</li>
        </ul>
        <div className="highlight-box blue">
          ℹ️ We do <strong>not</strong> use your data for advertising, sell it to third parties, or use it for any purpose beyond delivering and improving the Tracklio Hub platform.
        </div>
      </>
    ),
  },
  {
    id: 3,
    title: "Data Sharing & Third Parties",
    content: (
      <>
        <p>We share your data only in the following limited circumstances:</p>
        <ul>
          <li><strong>Payment Gateways:</strong> Razorpay, Cashfree, Paytm, PhonePe — for processing subscription payments. Governed by their respective privacy policies.</li>
          <li><strong>SMS Services:</strong> Twilio, Airtel AIO, Fast2SMS — for OTP and transactional SMS delivery.</li>
          <li><strong>Email Services:</strong> Google (Gmail/OAuth2), Nodemailer — for transactional email delivery.</li>
          <li><strong>Firebase (Google):</strong> For push notifications (FCM) to enrolled MDM devices.</li>
          <li><strong>Cloud Infrastructure:</strong> Servers hosted in India. Data is stored on secure infrastructure with restricted access.</li>
          <li><strong>Legal Obligations:</strong> If required by Indian law, court order, or government authority.</li>
          <li><strong>White-Label Clients:</strong> Your organization's IT administrators can access data relevant to their subscribed modules only.</li>
        </ul>
        <div className="highlight-box yellow">
          ⚠️ We never sell, rent, or trade your personal or business data to any third party for marketing or commercial purposes.
        </div>
      </>
    ),
  },
  {
    id: 4,
    title: "Data Storage & Security",
    content: (
      <>
        <p>We take data security seriously and implement the following measures:</p>
        <ul>
          <li>All data transmitted between your browser/app and our servers is encrypted via <strong>HTTPS/TLS</strong>.</li>
          <li>Passwords are stored using industry-standard hashing (bcrypt). We never store plain-text passwords.</li>
          <li>Database access is restricted via IP whitelisting and role-based access controls.</li>
          <li>All servers are located in <strong>India</strong>.</li>
          <li>Regular security audits and vulnerability assessments are conducted.</li>
          <li>Multi-tenant data is isolated using <code>companyId</code> scoping — one tenant cannot access another's data.</li>
          <li>APK files and large assets are stored on secure disk storage with access controls.</li>
        </ul>
        <div className="highlight-box red">
          🔒 Despite our best efforts, no system is 100% secure. In the event of a data breach affecting your account, we will notify you within 72 hours as required by applicable law.
        </div>
      </>
    ),
  },
  {
    id: 5,
    title: "MDM & Device Location Data",
    content: (
      <>
        <p>For customers using the <strong>Mobile Device Management (MDM)</strong> module:</p>
        <ul>
          <li>Device location (GPS) is collected <strong>only</strong> when explicitly enabled by the organization's IT administrator.</li>
          <li>Location data is used solely for asset tracking of <strong>company-owned devices</strong>.</li>
          <li>Device data (IMEI, serial number, location logs) is accessible only to authorized IT administrators of the enrolling organization.</li>
          <li>Location tracking is <strong>not</strong> performed on personal devices or without enterprise enrollment.</li>
          <li>All MDM data is deleted within 30 days of device unenrollment.</li>
        </ul>
      </>
    ),
  },
  {
    id: 6,
    title: "Cookies & Tracking",
    content: (
      <>
        <p>Tracklio Hub uses the following types of cookies:</p>
        <ul>
          <li><strong>Essential Cookies:</strong> Required for authentication (JWT session tokens), CSRF protection, and core platform functionality. Cannot be disabled.</li>
          <li><strong>Functional Cookies:</strong> Store your preferences such as language, theme settings, and sidebar state.</li>
          <li><strong>Analytics Cookies:</strong> Help us understand how features are used to improve the platform. No personally identifiable data is shared with analytics providers.</li>
        </ul>
        <p>We do <strong>not</strong> use advertising cookies or third-party tracking pixels.</p>
      </>
    ),
  },
  {
    id: 7,
    title: "Data Retention",
    content: (
      <>
        <ul>
          <li><strong>Active Accounts:</strong> Data is retained for the duration of your subscription plus 90 days after cancellation.</li>
          <li><strong>Deleted Accounts:</strong> Upon account deletion request, data is permanently purged within 30 days.</li>
          <li><strong>Billing Records:</strong> Invoice and payment records are retained for 7 years as required by Indian GST law.</li>
          <li><strong>MDM Device Data:</strong> Deleted within 30 days of device unenrollment.</li>
          <li><strong>Audit Logs:</strong> Retained for 1 year for security and compliance purposes.</li>
          <li><strong>Support Communications:</strong> Retained for 2 years.</li>
        </ul>
      </>
    ),
  },
  {
    id: 8,
    title: "Your Rights",
    content: (
      <>
        <p>As a user of Tracklio Hub, you have the following rights regarding your data:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of all personal data we hold about you.</li>
          <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
          <li><strong>Deletion:</strong> Request deletion of your account and associated data (subject to legal retention requirements).</li>
          <li><strong>Portability:</strong> Export your business data in CSV/JSON format from within the platform.</li>
          <li><strong>Objection:</strong> Object to specific processing activities.</li>
          <li><strong>Withdrawal:</strong> Withdraw consent for optional data processing (e.g., analytics cookies).</li>
        </ul>
        <p>To exercise any of these rights, email us at <a href="mailto:privacy@trackliohub.com">privacy@trackliohub.com</a>. We respond within 14 business days.</p>
      </>
    ),
  },
  {
    id: 9,
    title: "Children's Privacy",
    content: (
      <p>Tracklio Hub is a B2B enterprise SaaS platform intended for use by businesses and working professionals. It is not directed at individuals under the age of 18. We do not knowingly collect personal data from minors. If we become aware that a minor has registered, we will promptly delete their account.</p>
    ),
  },
  {
    id: 10,
    title: "Changes to This Policy",
    content: (
      <p>We may update this Privacy Policy periodically to reflect changes in our practices, legal requirements, or platform features. Material changes will be communicated via email to registered users and posted on this page with an updated revision date. Continued use of the platform after changes constitutes acceptance of the updated policy.</p>
    ),
  },
];

const PrivacyPolicyWeb = () => {
  const [open, setOpen] = useState(null);
  const toggle = (id) => setOpen(open === id ? null : id);

  return (
    <div style={s.page}>
      <style>{css}</style>

      <div style={s.wrap}>
        {/* Hero */}
        <div style={s.hero}>
          <div style={s.badge}>Privacy Policy</div>
          <h1 style={s.h1}>Your Privacy Matters to Us</h1>
          <p style={s.heroSub}>
            Tracklio Hub is committed to protecting the privacy and security of your business data.
            This policy explains what we collect, how we use it, and your rights.
          </p>
          <p style={s.heroDate}>Last updated: June 25, 2026 &nbsp;·&nbsp; Effective: June 25, 2026</p>

          <div style={s.pillRow}>
            {["🔒 HTTPS Encrypted","🇮🇳 India Servers","🚫 No Data Selling","✅ GDPR Aligned"].map(p => (
              <span key={p} style={s.pill}>{p}</span>
            ))}
          </div>
        </div>

        {/* Scope banner */}
        <div style={s.scopeBanner}>
          <span style={s.scopeIcon}>🏢</span>
          <div>
            <strong>Scope of this Policy</strong>
            <p style={s.scopeText}>
              This policy applies to the Tracklio Hub web platform at <strong>trackliohub.com</strong> and
              all associated SaaS modules including CRM, HRMS, Hospital Management, MDM, Inventory,
              Wallet, White-Label, Orders, Reports, and any sub-applications or APIs offered under the
              Tracklio Hub brand.
            </p>
          </div>
        </div>

        {/* Accordion */}
        <div style={s.accordion}>
          {sections.map((sec) => (
            <div key={sec.id} style={s.accItem} className="acc-item">
              <button style={s.accBtn} onClick={() => toggle(sec.id)} className="acc-btn">
                <span style={s.accNum}>0{sec.id}</span>
                <span style={s.accTitle}>{sec.title}</span>
                <span style={{ ...s.accChev, transform: open === sec.id ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
              </button>
              {open === sec.id && (
                <div style={s.accBody} className="acc-body">
                  {sec.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact */}
        <div style={s.contactCard}>
          <h2 style={s.contactTitle}>Contact Our Privacy Team</h2>
          <p style={s.contactSub}>For privacy-related requests, data access, or concerns:</p>
          <div style={s.contactGrid}>
            <a href="mailto:privacy@trackliohub.com" style={s.contactItem}>📧 privacy@trackliohub.com</a>
            <a href="https://trackliohub.com" style={s.contactItem}>🌐 trackliohub.com</a>
            <span style={s.contactItem}>📍 Delhi, India</span>
            <span style={s.contactItem}>⏱ Response within 14 business days</span>
          </div>
        </div>

        <p style={s.footer}>© 2026 Tracklio Hub · All rights reserved</p>
      </div>
    </div>
  );
};

const s = {
  page: { minHeight:"100vh", background:"#f1f5f9", padding:"32px 16px", fontFamily:"'Segoe UI',Roboto,sans-serif" },
  wrap: { maxWidth:860, margin:"0 auto" },

  hero: { background:"linear-gradient(135deg,#1e40af 0%,#1e293b 100%)", borderRadius:16, padding:"52px 40px", textAlign:"center", color:"#fff", marginBottom:20 },
  badge: { display:"inline-block", backgroundColor:"rgba(255,255,255,0.15)", padding:"6px 18px", borderRadius:20, fontSize:13, fontWeight:600, letterSpacing:1, marginBottom:18, textTransform:"uppercase" },
  h1: { fontSize:32, fontWeight:800, margin:"0 0 14px", lineHeight:1.2 },
  heroSub: { fontSize:16, color:"rgba(255,255,255,0.8)", lineHeight:1.7, maxWidth:600, margin:"0 auto 16px" },
  heroDate: { fontSize:13, color:"rgba(255,255,255,0.5)", margin:"0 0 24px" },
  pillRow: { display:"flex", flexWrap:"wrap", gap:10, justifyContent:"center" },
  pill: { backgroundColor:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.2)", padding:"6px 14px", borderRadius:20, fontSize:13, color:"#fff" },

  scopeBanner: { display:"flex", gap:16, alignItems:"flex-start", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:12, padding:"18px 22px", marginBottom:20 },
  scopeIcon: { fontSize:24, flexShrink:0 },
  scopeText: { fontSize:14, color:"#1e3a5f", margin:"4px 0 0", lineHeight:1.7 },

  accordion: { display:"flex", flexDirection:"column", gap:8, marginBottom:24 },
  accItem: { background:"#fff", borderRadius:12, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.07)" },
  accBtn: { width:"100%", display:"flex", alignItems:"center", gap:14, padding:"18px 22px", border:"none", background:"none", cursor:"pointer", textAlign:"left" },
  accNum: { fontSize:11, fontWeight:700, color:"#3b82f6", backgroundColor:"#eff6ff", padding:"3px 8px", borderRadius:6, flexShrink:0 },
  accTitle: { flex:1, fontSize:15, fontWeight:600, color:"#1e293b" },
  accChev: { fontSize:18, color:"#64748b", transition:"transform 0.2s", flexShrink:0 },
  accBody: { padding:"4px 22px 22px 22px", fontSize:14, color:"#374151", lineHeight:1.8, borderTop:"1px solid #f1f5f9" },

  contactCard: { background:"linear-gradient(135deg,#1e40af,#1e293b)", borderRadius:14, padding:"36px 32px", textAlign:"center", color:"#fff", marginBottom:20 },
  contactTitle: { fontSize:22, fontWeight:700, margin:"0 0 8px" },
  contactSub: { fontSize:14, color:"rgba(255,255,255,0.7)", margin:"0 0 24px" },
  contactGrid: { display:"flex", flexWrap:"wrap", gap:12, justifyContent:"center" },
  contactItem: { backgroundColor:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.2)", padding:"10px 20px", borderRadius:30, fontSize:14, color:"#fff", textDecoration:"none" },

  footer: { textAlign:"center", fontSize:13, color:"#94a3b8", marginTop:8 },
};

const css = `
  .acc-btn:hover { background:#f8faff !important; }
  .acc-body ul { padding-left:22px; margin:8px 0; }
  .acc-body li { margin-bottom:8px; }
  .acc-body a { color:#3b82f6; }
  .acc-body code { background:#f1f5f9; padding:2px 6px; border-radius:4px; font-size:12px; }
  .info-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(240px,1fr)); gap:12px; margin-top:14px; }
  .info-card { background:#f8faff; border:1px solid #dbeafe; border-radius:10px; padding:16px; }
  .info-card .info-icon { font-size:22px; margin-bottom:8px; }
  .info-card strong { display:block; font-size:13px; color:#1e40af; margin-bottom:6px; }
  .info-card p { font-size:13px; color:#374151; margin:0; line-height:1.6; }
  .highlight-box { padding:14px 18px; border-radius:8px; margin-top:14px; font-size:14px; line-height:1.7; }
  .highlight-box.blue { background:#eff6ff; border:1px solid #bfdbfe; color:#1e3a5f; }
  .highlight-box.yellow { background:#fffbeb; border:1px solid #fde68a; color:#78350f; }
  .highlight-box.red { background:#fef2f2; border:1px solid #fecaca; color:#7f1d1d; }
`;

export default PrivacyPolicyWeb;